import React, { useEffect, useState, useMemo } from "react";
import { apiGet, apiDelete } from "../../../api";
import { FiTrash2, FiMessageSquare, FiChevronDown, FiChevronRight } from "react-icons/fi";
import "../styles/MyFriends.css"; // Import the new CSS file

const TABS = ["All", "F", "W", "S", "Y"];
const termLabel = (t) => {
  switch(t) {
    case "F": return "Fall";
    case "W": return "Winter"; 
    case "S": return "Summer";
    case "Y": return "Year-long";
    default: return "All";
  }
};
const anonymizeEmail = (email) => (email && email.includes('@') ? email.split('@')[0] : email);

export default function MyFriends() {
  const [friends, setFriends] = useState([]);
  const [msg, setMsg] = useState("");
  const [tab, setTab] = useState("All");
  const [confirmRemove, setConfirmRemove] = useState(null);
  const [expanded, setExpanded] = useState({}); // For collapsible state

  const load = async () => {
    try {
      const data = await apiGet("/friends");
      setFriends(data.friends || []);
    } catch (e) {
      setMsg(e.message || "Failed to load friends");
    }
  };

  useEffect(() => { load(); }, []);

  const removeFriend = async (id) => {
    try {
      await apiDelete(`/friends/${id}`);
      setConfirmRemove(null);
      await load();
    } catch (e) {
      setMsg(e.message || "Failed to remove friend");
    }
  };

const visibleFriends = useMemo(() => {
  if (tab === "All") {
    return [...friends];
  } else if (tab === "F") {
    // Include friends with F courses OR Y courses
    return friends.filter(f => 
      f.sharedByTerm?.F || f.sharedByTerm?.Y
    );
  } else if (tab === "W") {
    // Include friends with W courses OR Y courses
    return friends.filter(f => 
      f.sharedByTerm?.W || f.sharedByTerm?.Y
    );
  } else {
    return friends.filter(f => f.sharedByTerm?.[tab]);
  }
}, [friends, tab]);

  // Logic for collapsing
  const useCollapsible = visibleFriends.length > 3;
  const toggleExpand = (id) => setExpanded(prev => ({ ...prev, [id]: !prev[id] }));

  const renderFriendContent = (f) => (
    <div className="friend-courses">
      {tab === "All" ? (
        <>
          <div className="course-summary">
            Total shared courses: <strong>{f.sharedCourseCount}</strong>
          </div>
          {f.sharedByTerm && Object.keys(f.sharedByTerm).length > 0 && (
            <div className="term-course-list">
              {Object.entries(f.sharedByTerm).map(([term, codes]) => (
                <div key={term}>
                  <strong className="term-label">{termLabel(term)}:</strong> {codes.join(", ")}
                </div>
              ))}
            </div>
          )}
        </>
      ) : (
        <>
          <div className="course-summary">
            Shared in {termLabel(tab)}: <strong>{f.sharedByTerm?.[tab]?.length || 0}</strong>
          </div>
          {f.sharedByTerm?.[tab]?.length > 0 && (
            <div className="term-course-list">
              {f.sharedByTerm[tab].join(", ")}
            </div>
          )}
        </>
      )}
    </div>
  );

  return (
    <div className="friends-container page-card">
      <div className="friends-header">
        <h2>My Friends</h2>
        <div className="tabs">
          {TABS.map((t) => (
            <button key={t} onClick={() => setTab(t)} className={`tab-btn ${tab === t ? "active" : ""}`}>
              {termLabel(t)}
            </button>
          ))}
        </div>
      </div>

      <div className="friends-content">
        {msg && <p className="status-message error">{msg}</p>}

        {visibleFriends.length === 0 ? (
          <p className="no-friends-msg">You haven't added any friends yet. Use "Find Classmates" to connect!</p>
        ) : (
          <div className="friends-list">
            {visibleFriends.map((f) => {
              const friendHeader = (
                <div className="friend-header">
                  <div className="friend-info">
                    {useCollapsible && (
                      <span className="chevron-icon">
                        {expanded[f.id] ? <FiChevronDown /> : <FiChevronRight />}
                      </span>
                    )}
                    <strong className="friend-name">{f.name || anonymizeEmail(f.email)}</strong>
                    {f.preferredPlatform && (
                      <span className="friend-platform">
                        <FiMessageSquare /> {f.preferredPlatform} {f.handle ? `(${f.handle})` : ""}
                      </span>
                    )}
                  </div>
                  {confirmRemove === f.id ? (
                    <div className="confirm-remove">
                      <span>Remove?</span>
                      <button className="btn btn-danger" onClick={(e) => { e.stopPropagation(); removeFriend(f.id); }}>Yes</button>
                      <button className="btn btn-secondary" onClick={(e) => { e.stopPropagation(); setConfirmRemove(null); }}>No</button>
                    </div>
                  ) : (
                    <button className="btn btn-danger" onClick={(e) => { e.stopPropagation(); setConfirmRemove(f.id); }}>
                      <FiTrash2 /> Remove
                    </button>
                  )}
                </div>
              );

              return useCollapsible ? (
                <details key={f.id} className="friend-card collapsible" onToggle={() => toggleExpand(f.id)} open={expanded[f.id]}>
                  <summary>{friendHeader}</summary>
                  {renderFriendContent(f)}
                </details>
              ) : (
                <div key={f.id} className="friend-card expanded">
                  {friendHeader}
                  {renderFriendContent(f)}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}